import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {L1L3AuthService} from '../l1-l3-auth.service';
import {TokenRefreshService} from '../token-refresh.service';
import {IToken} from "../../model/token";

@Injectable({providedIn: 'root'})
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenSubject: BehaviorSubject<any>;

  constructor(private l1l3AuthService: L1L3AuthService,
              private tokenRefreshService: TokenRefreshService) {
    this.refreshTokenSubject = this.tokenRefreshService.getRefreshTokenSubject();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isSecuredHost(request.url)) {
      request = this.addAuthToken(request);
    }

    return this.processRequest(request, next);

  }

  private handleResponseError(err: any, request: any, next: any): Observable<HttpEvent<any>> {
    // we ignore the token and logout endpoint because the token errors are handled in the refresh logic itself
    // and the errors on logout could cause loops or too many logouts from the l1
    // (by the refresh logic and by the logout call itself)
    // --> on the first logout the tokens are deleted and on the next logout the id token is gone already which is bad
    // if logging out from the l3 server is needed to work at any time (e.g. to really have the updated permissions)
    // then we should no logout from l3 and l1 in parallel
    if (this.isAuthenticationError(err) && !this.isTokenEndpoint(request) && !this.isL3LogoutEndpoint(request)) {
      return this.handle401Error(err, request, next);
    } else {
      return throwError(err);
    }
  }

  private handle401Error(err: any, request: any, next: any): Observable<any> {
    if (!this.tokenRefreshService.isRefreshTokenPending()) {
      console.log('401 - starting refresh:', request);
      this.tokenRefreshService.startRefresh();
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => {
        console.log('Fire waiting request: ', request);
        return this.processRequest(this.addAuthToken(request), next);
      }));
  }

  private processRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(error => {
      return this.handleResponseError(error, request, next);
    }));
  }

  private isAuthenticationError(error: any): boolean {
    return error instanceof HttpErrorResponse && error?.status === 401;
  }

  private is400Error(error: any): boolean {
    return error instanceof HttpErrorResponse && error?.status === 400;
  }

  private isSecuredHost(url: string): boolean {
    return this.l1l3AuthService.securedHosts.some(n => url.includes(n));
  }

  private addAuthToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const tokenL1: IToken = this.l1l3AuthService.getL1Token();

    if (!tokenL1) {
      return request;
    }

    request = request.clone({
      setHeaders: {
        'X-AUTH-TOKEN': tokenL1.access_token,
      }
    });

    const tokenL3: IToken = this.l1l3AuthService.getL3Token();

    if (!tokenL3 || request.url.indexOf(this.l1l3AuthService.l3Config?.paths?.auth) !== -1) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${tokenL3.access_token}`
      }
    });
  }

  isTokenEndpoint(request: any): boolean {
    console.log('Is token endpoint: ', request?.url);
    let isTokenEndpoint = request?.url?.indexOf(this.l1l3AuthService.l1Config?.host + this.l1l3AuthService.l1Config.paths.token)
      > -1 || request?.url?.indexOf(this.l1l3AuthService.l3Config?.host + this.l1l3AuthService.l3Config.paths.auth) > -1;
    console.log("---> ", isTokenEndpoint);
    return isTokenEndpoint;
  }

  private isL3LogoutEndpoint(request: any) {
    return request.url?.indexOf(this.l1l3AuthService.l3Config?.host + this.l1l3AuthService.l3Config?.paths?.logout) > -1;
  }
}
